// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from '../firebase/config';
import { 
  onAuthStateChanged, 
  setPersistence, 
  browserLocalPersistence 
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  // ✅ Activer la persistance Firebase au démarrage.
  // browserLocalPersistence fait déjà ce que le bloc localStorage "maison"
  // (supprimé ci-dessous) essayait de refaire à la main — mais correctement,
  // et en se synchronisant automatiquement entre onglets.
  useEffect(() => {
    const enablePersistence = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        console.log("✅ Persistance Firebase activée (local)");
      } catch (err) {
        console.error("❌ Erreur activation persistance:", err);
      }
    };
    enablePersistence();
  }, []);

  // ❌ SUPPRIMÉ : restauration manuelle depuis localStorage('adminUser').
  // Ce mécanisme créait un faux "user" ({uid, email} sans lien avec Firebase
  // Auth) valide 24h, jamais invalidé au logout, et court-circuitait
  // onAuthStateChanged — c'est ce qui causait la reconnexion fantôme dans
  // les nouveaux onglets après déconnexion. browserLocalPersistence +
  // onAuthStateChanged ci-dessous suffisent amplement.

  // ✅ Écouter les changements d'état Firebase — SEULE source de vérité
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("🔄 onAuthStateChanged:", firebaseUser?.email || "Aucun utilisateur");

      if (firebaseUser) {
        setUser(firebaseUser);
        setError(null);

        // Récupérer le rôle depuis Firestore
        setRoleLoading(true);
        try {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const userRole = userData.userRole || "User";
            setRole(userRole);
          } else {
            setRole(null);
            console.warn("⚠️ Document utilisateur non trouvé");
          }
        } catch (err) {
          console.error("❌ Erreur récupération rôle:", err);
          setRoleError(err);
        } finally {
          setRoleLoading(false);
        }
      } else {
        // ✅ Aucun utilisateur Firebase → état propre et immédiat,
        // plus de condition foireuse sur l'ancien `user` en closure.
        console.log("🔓 Aucune session Firebase active");
        setUser(null);
        setRole(null);
        setRoleLoading(false);
        localStorage.removeItem('adminUser'); // nettoyage d'un éventuel résidu d'ancienne version
      }

      setLoading(false);
      setAuthInitialized(true);
    });

    return () => unsubscribe();
  }, []); // ✅ plus de dépendance sur `user` — supprime aussi une resubscription inutile à chaque changement

  const value = {
    user,
    loading,
    error,
    role,
    roleLoading,
    roleError,
    isAdmin: role === "Admin",
    isAuthenticated: !!user,
    authInitialized
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;